import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { CcSyncLine } from "@/components/CcSyncLine";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Plus, FolderGit2, Server, FileText, ArrowRight, Trash2, Calendar, Hash, UserCog, Trophy, Link2, ShieldCheck, Rocket, Target,
} from "lucide-react";

const STATUS_META = {
  won: { label: "Won", cls: "text-[#00FF41] border-[#00FF41]/30 bg-[#00FF41]/10" },
  lost: { label: "Lost", cls: "text-[#FF3B30] border-[#FF3B30]/30 bg-[#FF3B30]/10" },
  open: { label: "Open", cls: "text-[#8B949E] border-white/20 bg-white/5" },
};

// Workflow stage shown on each card so the pipeline position is visible without opening the project.
const STAGE_META = {
  confirmed: { label: "Confirmed — not started", cls: "text-[#B084FF] border-[#B084FF]/30 bg-[#B084FF]/10" },
  sow_in_progress: { label: "SOW in progress", cls: "text-[#EAE04A] border-[#EAE04A]/30 bg-[#EAE04A]/10" },
  sow_ready: { label: "SOW ready", cls: "text-[#00FF41] border-[#00FF41]/30 bg-[#00FF41]/10" },
  config_started: { label: "Config started", cls: "text-[#00F0FF] border-[#00F0FF]/30 bg-[#00F0FF]/10" },
  deployment_ready: { label: "Deployment ready", cls: "text-[#00FF41] border-[#00FF41]/30 bg-[#00FF41]/10" },
  config_failed: { label: "Config failed", cls: "text-[#FF3B30] border-[#FF3B30]/30 bg-[#FF3B30]/10" },
  created: { label: "Created", cls: "text-[#8B949E] border-white/20 bg-white/5" },
  scoping: { label: "Scoping", cls: "text-[#EAE04A] border-[#EAE04A]/30 bg-[#EAE04A]/10" },
  assessment_ready: { label: "Assessment ready", cls: "text-[#00FF41] border-[#00FF41]/30 bg-[#00FF41]/10" },
  in_delivery: { label: "In delivery", cls: "text-[#00F0FF] border-[#00F0FF]/30 bg-[#00F0FF]/10" },
  asbuilt_ready: { label: "As-Built handed over", cls: "text-[#00FF41] border-[#00FF41]/30 bg-[#00FF41]/10" },
};

// Order + friendly titles for the "group by stage" pipeline view.
const STAGE_ORDER = [
  "confirmed", "sow_in_progress", "sow_ready", "config_started", "config_failed",
  "deployment_ready", "in_delivery", "asbuilt_ready", "scoping", "assessment_ready", "created",
];

function fmtDateTime(s) {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString("en-AU", {
      timeZone: "Australia/Sydney", year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", timeZoneName: "short",
    });
  } catch { return "—"; }
}

export default function Dashboard() {
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", project_type: "presales", ps_code: "", client: "", description: "" });
  const [creating, setCreating] = useState(false);
  const [transferProj, setTransferProj] = useState(null);
  const [transferTo, setTransferTo] = useState("");
  const [transferring, setTransferring] = useState(false);
  const [health, setHealth] = useState(null);
  const [groupBy, setGroupBy] = useState("type");
  const [sortBy, setSortBy] = useState("newest");
  const [stageFilter, setStageFilter] = useState("all");
  const navigate = useNavigate();
  const { isAdmin, user } = useAuth();

  const canCreate = isAdmin || !!user?.can_create_presales;

  const load = async () => {
    try {
      const { data } = await api.get("/projects");
      setProjects(data);
    } catch (e) {
      toast.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (isAdmin) api.get("/admin/users").then(({ data }) => setUsers(data)).catch(() => {});
    if (isAdmin) {
      const loadHealth = () => api.get("/admin/integration-health").then(({ data }) => setHealth(data)).catch(() => {});
      loadHealth();
      const t = setInterval(loadHealth, 30000);
      return () => clearInterval(t);
    }
  }, [isAdmin]);

  useEffect(() => {
    // Non-admins with access can only create Pre-Sales manually.
    if (open && !isAdmin) setForm((f) => ({ ...f, project_type: "presales" }));
  }, [open, isAdmin]);

  const createProject = async () => {
    if (!form.name.trim()) { toast.error("Project name is required"); return; }
    if (form.project_type === "delivery" && !form.ps_code.trim()) { toast.error("PS Code is required"); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/projects", form);
      if (data.version > 1) {
        toast.success(`Project created. PS Code "${data.ps_code}" already exists — saved as version ${data.version}.`);
      } else {
        toast.success("Project created");
      }
      setOpen(false);
      setForm({ name: "", project_type: "presales", ps_code: "", client: "", description: "" });
      navigate(`/project/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create project");
    } finally {
      setCreating(false);
    }
  };

  const deleteProject = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("Delete this project and all its documents?")) return;
    try {
      await api.delete(`/projects/${id}`);
      setProjects((p) => p.filter((x) => x.id !== id));
      toast.success("Project deleted");
    } catch { toast.error("Delete failed"); }
  };

  const openTransfer = (e, p) => {
    e.stopPropagation();
    setTransferProj(p);
    setTransferTo(p.owner_id || "");
  };

  const doTransfer = async () => {
    if (!transferTo) { toast.error("Select a user to transfer to"); return; }
    setTransferring(true);
    try {
      const { data } = await api.post(`/admin/projects/${transferProj.id}/transfer`, { owner_id: transferTo });
      toast.success(`Ownership transferred to ${data.owner_name || data.owner_email}`);
      setTransferProj(null);
      setTransferTo("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Transfer failed");
    } finally {
      setTransferring(false);
    }
  };

  const ProjectCard = ({ p, i }) => (
    <div
      key={p.id}
      data-testid={`project-card-${p.id}`}
      onClick={() => navigate(`/project/${p.id}`)}
      style={{ animationDelay: `${i * 60}ms` }}
      className="fade-up group cursor-pointer border border-white/10 bg-[#0B0C10] p-6 hover:border-[#00F0FF]/40 transition-colors duration-200"
    >
      <div className="flex items-start justify-between">
        <FolderGit2 className="text-[#00F0FF]" size={22} />
        <div className="flex items-center gap-2">
          {isAdmin && (
            <button data-testid={`transfer-project-${p.id}`} onClick={(e) => openTransfer(e, p)} title="Transfer owner"
              className="text-[#484F58] hover:text-[#00F0FF] transition-colors duration-200">
              <UserCog size={16} />
            </button>
          )}
          {isAdmin && (
            <button data-testid={`delete-project-${p.id}`} onClick={(e) => deleteProject(e, p.id)}
              className="text-[#484F58] hover:text-[#FF3B30] transition-colors duration-200">
              <Trash2 size={16} />
            </button>
          )}
        </div>
      </div>
      <h3 className="font-mono-jb text-xl font-bold text-white mt-4 tracking-tight truncate">{p.name}</h3>
      <div className="flex items-center gap-2 mt-1 flex-wrap">
        <span data-testid={`project-type-${p.id}`} className={`text-[9px] uppercase tracking-[0.15em] font-mono-jb px-1.5 py-0.5 rounded-sm border ${p.project_type === "presales" ? "text-[#EAE04A] border-[#EAE04A]/30 bg-[#EAE04A]/10" : p.project_type === "assessment" ? "text-[#FF7A59] border-[#FF7A59]/30 bg-[#FF7A59]/10" : "text-[#00F0FF] border-[#00F0FF]/30 bg-[#00F0FF]/10"}`}>
          {p.project_type === "presales" ? "Pre-Sales" : p.project_type === "assessment" ? "Assessment" : "Delivery"}
        </span>
        {p.project_type === "presales" && (
          <span data-testid={`project-status-${p.id}`} className={`text-[9px] uppercase tracking-[0.15em] font-mono-jb px-1.5 py-0.5 rounded-sm border ${(STATUS_META[p.status] || STATUS_META.open).cls}`}>
            {(STATUS_META[p.status] || STATUS_META.open).label}
          </span>
        )}
        {p.workflow?.stage && STAGE_META[p.workflow.stage] && (
          <span data-testid={`project-stage-${p.id}`} title={p.workflow.blocked_reason || undefined}
            className={`text-[9px] uppercase tracking-[0.15em] font-mono-jb px-1.5 py-0.5 rounded-sm border ${STAGE_META[p.workflow.stage].cls}`}>
            {STAGE_META[p.workflow.stage].label}
            {p.workflow.pending_count > 0 && p.workflow.stage === "sow_in_progress" ? ` · ${p.workflow.pending_count} to approve` : ""}
          </span>
        )}
        {p.cc_opportunity_id && (
          <span data-testid={`project-cc-${p.id}`} title={`Command Centre opportunity ${p.cc_opportunity_id}`}
            className="text-[9px] uppercase tracking-[0.15em] font-mono-jb px-1.5 py-0.5 rounded-sm border text-[#B084FF] border-[#B084FF]/30 bg-[#B084FF]/10 flex items-center gap-1">
            <Link2 size={10} /> Command Centre
          </span>
        )}
        <span className="text-xs text-[#8B949E] truncate">{p.client || "—"}</span>
      </div>
      {p.ps_code && (
        <div className="flex items-center gap-1.5 mt-2 text-[11px] font-mono-jb text-[#00FF41]" data-testid={`project-pscode-${p.id}`}>
          <Hash size={12} /> {p.ps_code}
          {p.version > 1 && (
            <span className="ml-1 px-1.5 py-0.5 rounded-sm bg-[#EAE04A]/15 text-[#EAE04A] text-[9px] font-bold" data-testid={`project-version-${p.id}`}>
              v{p.version}
            </span>
          )}
        </div>
      )}
      {isAdmin && (
        <div className="mt-3 pt-3 border-t border-white/10 space-y-1.5 font-mono-jb text-[11px] text-[#8B949E]" data-testid={`project-admin-meta-${p.id}`}>
          <div className="flex items-center gap-1.5" title={p.owner_email}>
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-sm bg-[#00F0FF]/15 text-[#00F0FF] text-[9px] font-bold">
              {p.owner_initials || "?"}
            </span>
            <span className="truncate">{p.owner_name || p.owner_email || "Unknown"}</span>
          </div>
          <div className="flex items-center gap-1.5"><Calendar size={11} className="text-[#484F58]" /> Created {fmtDateTime(p.created_at)}</div>
          <div className="flex items-center gap-1.5"><Calendar size={11} className="text-[#484F58]" /> Version {fmtDateTime(p.updated_at)}</div>
        </div>
      )}
      <div className="flex items-center gap-4 mt-5 pt-4 border-t border-white/10 font-mono-jb text-xs text-[#8B949E]">
        <span className="flex items-center gap-1.5"><Server size={13} className="text-[#00FF41]" />{p.device_count} devices</span>
        <span className="flex items-center gap-1.5"><FileText size={13} className="text-[#EAE04A]" />{p.doc_count} docs</span>
        <ArrowRight size={15} className="ml-auto text-[#484F58] group-hover:text-[#00F0FF] group-hover:translate-x-1 transition-all duration-200" />
      </div>
      {p.cc_opportunity_id && (
        <div className="mt-3 pt-3 border-t border-white/10">
          <CcSyncLine sync={p.cc_sync} opportunityId={p.cc_opportunity_id} onRetried={load} />
        </div>
      )}
    </div>
  );

  const Section = ({ id, icon: Icon, title, tint, list }) => {
    if (list.length === 0) return null;
    return (
      <section data-testid={`stage-${id}`} className="mb-10">
        <div className="flex items-center gap-2 mb-4">
          <Icon size={16} style={{ color: tint }} />
          <h2 className="font-mono-jb text-sm uppercase tracking-[0.18em] text-white">{title}</h2>
          <span className="font-mono-jb text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-white/15 text-[#8B949E]">{list.length}</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {list.map((p, i) => <ProjectCard key={p.id} p={p} i={i} />)}
        </div>
      </section>
    );
  };

  const stageOf = (p) => p.workflow?.stage || (p.status === "won" ? "config_started" : "");
  const sortProjects = (list) => {
    const s = [...list];
    if (sortBy === "name") s.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    else if (sortBy === "oldest") s.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    else s.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    return s;
  };
  const matchesStage = (p) => stageFilter === "all" || stageOf(p) === stageFilter;

  const visible = sortProjects(projects.filter(matchesStage));
  const presalesActive = visible.filter((p) => p.project_type === "presales" && p.status !== "won");
  const baseline = visible.filter((p) => p.project_type === "presales" && p.status === "won");
  const assessment = visible.filter((p) => p.project_type === "assessment");
  const delivery = visible.filter((p) => p.project_type === "delivery");
  // Group-by-stage view: one section per workflow stage, in pipeline order.
  const stageGroups = STAGE_ORDER
    .map((st) => ({ st, list: visible.filter((p) => stageOf(p) === st) }))
    .filter((g) => g.list.length > 0);
  // Stages actually present across all projects (for the filter dropdown).
  const presentStages = STAGE_ORDER.filter((st) => projects.some((p) => stageOf(p) === st));

  return (
    <div className="p-8 lg:p-10">
      {/* Header */}
      <div className="flex items-end justify-between mb-8 border-b border-white/10 pb-6">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-[#484F58] mb-2 font-mono-jb">Workspace</div>
          <h1 className="font-mono-jb text-4xl sm:text-5xl font-extrabold tracking-tighter text-white">
            Projects<span className="text-[#00F0FF] cursor-blink">_</span>
          </h1>
          <p className="text-[#8B949E] text-sm mt-3 max-w-2xl leading-relaxed">
            Projects flow in from <span className="text-[#B084FF]">Command Centre</span> at scoping, move through the Pre-Sales pack &amp; approvals to a locked Baseline on WON, then a Delivery project is created after the config build passes. <span className="text-[#FF7A59]">Assessment</span> engagements review a customer's own environment against ISM &amp; Essential Eight.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {canCreate && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="new-project-button"
                className="rounded-sm bg-[#00F0FF] text-black font-mono-jb uppercase text-xs tracking-widest font-bold hover:bg-[#00F0FF] hover:-translate-y-0.5 hover:glow-cyan transition-transform duration-200 h-11 px-5"
              >
                <Plus size={16} className="mr-1" /> {isAdmin ? "Manual Project" : "New Pre-Sales"}
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0B0C10] border-white/10 text-white">
              <DialogHeader>
                <DialogTitle className="font-mono-jb uppercase tracking-wider text-[#00F0FF]">
                  {isAdmin ? "Manual Project (Admin Fallback)" : "New Pre-Sales Project"}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <p className="text-[11px] text-[#EAE04A]/80 font-mono-jb leading-relaxed border border-[#EAE04A]/20 bg-[#EAE04A]/5 p-2.5 rounded-sm">
                  Projects normally arrive automatically from Command Centre. Use this only for a test bench, an ad-hoc job, or migrating an old deal.
                </p>
                {isAdmin && (
                  <div>
                    <label className="text-xs tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">Project Type</label>
                    <div className="mt-2 grid grid-cols-3 gap-2">
                      <button type="button" data-testid="type-delivery"
                        onClick={() => setForm({ ...form, project_type: "delivery" })}
                        className={`border p-3 text-left transition-colors duration-200 ${form.project_type === "delivery" ? "border-[#00F0FF]/60 bg-[#00F0FF]/5" : "border-white/10 hover:border-white/25"}`}>
                        <div className="font-mono-jb text-sm font-bold" style={{ color: form.project_type === "delivery" ? "#00F0FF" : "#fff" }}>Delivery</div>
                        <div className="text-[10px] text-[#8B949E] mt-0.5">Document what's deployed</div>
                      </button>
                      <button type="button" data-testid="type-presales"
                        onClick={() => setForm({ ...form, project_type: "presales" })}
                        className={`border p-3 text-left transition-colors duration-200 ${form.project_type === "presales" ? "border-[#EAE04A]/60 bg-[#EAE04A]/5" : "border-white/10 hover:border-white/25"}`}>
                        <div className="font-mono-jb text-sm font-bold" style={{ color: form.project_type === "presales" ? "#EAE04A" : "#fff" }}>Pre-Sales</div>
                        <div className="text-[10px] text-[#8B949E] mt-0.5">Win the deal</div>
                      </button>
                      <button type="button" data-testid="type-assessment"
                        onClick={() => setForm({ ...form, project_type: "assessment" })}
                        className={`border p-3 text-left transition-colors duration-200 ${form.project_type === "assessment" ? "border-[#FF7A59]/60 bg-[#FF7A59]/5" : "border-white/10 hover:border-white/25"}`}>
                        <div className="font-mono-jb text-sm font-bold" style={{ color: form.project_type === "assessment" ? "#FF7A59" : "#fff" }}>Assessment</div>
                        <div className="text-[10px] text-[#8B949E] mt-0.5">Review & certify env</div>
                      </button>
                    </div>
                  </div>
                )}
                <div>
                  <label className="text-xs tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">{form.project_type === "presales" ? "Opportunity Name" : "Project Name"}</label>
                  <Input data-testid="project-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder={form.project_type === "presales" ? "Acme Corp SD-WAN Refresh" : "DC-Core Firewall Refresh"} className="mt-2 bg-[#0B0C10] border-white/15 font-mono-jb focus-visible:ring-[#00F0FF]" />
                </div>
                {form.project_type === "delivery" && (
                  <div>
                    <label className="text-xs tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">PS Code</label>
                    <Input data-testid="project-pscode-input" value={form.ps_code} onChange={(e) => setForm({ ...form, ps_code: e.target.value })}
                      placeholder="Unique PS code from the config generator" className="mt-2 bg-[#0B0C10] border-white/15 font-mono-jb focus-visible:ring-[#00F0FF]" />
                    <p className="text-[10px] text-[#484F58] mt-1.5 font-mono-jb">Enter the unique PS code you obtained from the config generator.</p>
                  </div>
                )}
                <div>
                  <label className="text-xs tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">Client</label>
                  <Input data-testid="project-client-input" value={form.client} onChange={(e) => setForm({ ...form, client: e.target.value })}
                    placeholder="Acme Corp" className="mt-2 bg-[#0B0C10] border-white/15 font-mono-jb focus-visible:ring-[#00F0FF]" />
                </div>
                <div>
                  <label className="text-xs tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">Description</label>
                  <Textarea data-testid="project-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Scope & objectives..." className="mt-2 bg-[#0B0C10] border-white/15 font-mono-jb focus-visible:ring-[#00F0FF]" />
                </div>
                {form.project_type === "presales" && (
                  <p className="text-[10px] text-[#EAE04A]/80 font-mono-jb">Next: fill in the project brief on the project page, then generate your pre-sales pack.</p>
                )}
              </div>
              <DialogFooter>
                <Button data-testid="create-project-submit" onClick={createProject} disabled={creating}
                  className="rounded-sm bg-[#00F0FF] text-black font-mono-jb uppercase text-xs tracking-widest font-bold hover:bg-[#00F0FF]">
                  {creating ? "Creating..." : "Create"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          )}
        </div>
      </div>

      {isAdmin && health && (
        <div className="flex flex-wrap gap-2 mb-8" data-testid="integration-health-strip">
          {[{ id: "cc", label: "Command Centre", node: health.cc }, { id: "cg", label: "Config Generator", node: health.config_generator }].map(({ id, label, node }) => (
            <button key={id} data-testid={`health-pill-${id}`} onClick={() => navigate("/integrations")}
              className="flex items-center gap-2 border border-white/10 bg-[#0B0C10] px-3 py-2 hover:border-[#00F0FF]/40 transition-colors duration-200">
              <span className={`inline-block w-2 h-2 rounded-full ${node?.inbound_healthy ? "bg-[#00FF41]" : "bg-[#FF3B30]"}`} />
              <span className="font-mono-jb text-xs text-white">{label}</span>
              {node?.fingerprint_match === true && <span className="text-[#00FF41] text-[10px] font-mono-jb">key ✓</span>}
              {node?.fingerprint_match === false && <span className="text-[#FF3B30] text-[10px] font-mono-jb">key ✗</span>}
              {node?.fingerprint_match == null && <span className="text-[#8B949E] text-[10px] font-mono-jb">no ping yet</span>}
              <ArrowRight size={12} className="text-[#484F58]" />
            </button>
          ))}
        </div>
      )}

      {!loading && (() => {
        const presales = projects.filter((p) => p.project_type === "presales");
        if (presales.length === 0) return null;
        const won = presales.filter((p) => p.status === "won").length;
        const lost = presales.filter((p) => p.status === "lost").length;
        const open = presales.length - won - lost;
        const decided = won + lost;
        const rate = decided ? Math.round((won / decided) * 100) : 0;
        const Stat = ({ label, value, color, testid }) => (
          <div data-testid={testid} className="border border-white/10 bg-[#0B0C10] px-5 py-4 flex-1 min-w-[130px]">
            <div className="font-mono-jb text-3xl font-extrabold tracking-tighter" style={{ color }}>{value}</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#8B949E] font-mono-jb mt-1">{label}</div>
          </div>
        );
        return (
          <div className="flex flex-wrap gap-3 mb-10" data-testid="winrate-stats">
            <div className="border border-[#00FF41]/30 bg-[#00FF41]/5 px-5 py-4 flex-1 min-w-[150px]">
              <div className="flex items-center gap-2">
                <Trophy size={20} className="text-[#00FF41]" />
                <div className="font-mono-jb text-3xl font-extrabold tracking-tighter text-[#00FF41]" data-testid="winrate-value">{rate}%</div>
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#8B949E] font-mono-jb mt-1">Win Rate {decided ? `(${won}/${decided} decided)` : "(no decisions yet)"}</div>
            </div>
            <Stat label="Won" value={won} color="#00FF41" testid="stat-won" />
            <Stat label="Lost" value={lost} color="#FF3B30" testid="stat-lost" />
            <Stat label="Open" value={open} color="#EAE04A" testid="stat-open" />
          </div>
        );
      })()}

      {loading ? (
        <div className="font-mono-jb text-[#8B949E] text-sm">Loading projects...</div>
      ) : projects.length === 0 ? (
        <div className="border border-white/10 bg-[#0B0C10] p-12 text-center scanlines" data-testid="empty-projects">
          <FolderGit2 className="mx-auto text-[#484F58] mb-4" size={48} />
          <div className="font-mono-jb text-lg text-white mb-1">No projects yet</div>
          <p className="text-[#8B949E] text-sm max-w-md mx-auto">
            Projects appear here when Command Centre sends one through at the scoping stage.
            {canCreate ? " You can also create one manually with the button above." : " Ask an admin if you need to create one manually."}
          </p>
        </div>
      ) : (
        <>
          {/* Pipeline controls: group by type/stage, filter by stage, sort */}
          <div className="flex flex-wrap items-center gap-3 mb-8" data-testid="pipeline-controls">
            <div className="flex items-center gap-1 border border-white/10 bg-[#0B0C10] p-0.5 rounded-sm" data-testid="groupby-toggle">
              {[["type", "By Type"], ["stage", "By Stage"]].map(([k, lbl]) => (
                <button key={k} data-testid={`groupby-${k}`} onClick={() => setGroupBy(k)}
                  className={`font-mono-jb text-[11px] uppercase tracking-wider px-3 py-1.5 rounded-sm transition-colors duration-200 ${groupBy === k ? "bg-[#00F0FF] text-black font-bold" : "text-[#8B949E] hover:text-white"}`}>
                  {lbl}
                </button>
              ))}
            </div>
            <Select value={stageFilter} onValueChange={setStageFilter}>
              <SelectTrigger data-testid="stage-filter" className="w-[190px] h-9 bg-[#0B0C10] border-white/15 font-mono-jb text-xs focus:ring-[#00F0FF]">
                <SelectValue placeholder="All stages" />
              </SelectTrigger>
              <SelectContent className="bg-[#0B0C10] border-white/15 text-white font-mono-jb">
                <SelectItem value="all" data-testid="stage-filter-all">All stages</SelectItem>
                {presentStages.map((st) => (
                  <SelectItem key={st} value={st} data-testid={`stage-filter-${st}`}>{STAGE_META[st]?.label || st}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger data-testid="sort-by" className="w-[150px] h-9 bg-[#0B0C10] border-white/15 font-mono-jb text-xs focus:ring-[#00F0FF]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0B0C10] border-white/15 text-white font-mono-jb">
                <SelectItem value="newest" data-testid="sort-newest">Newest first</SelectItem>
                <SelectItem value="oldest" data-testid="sort-oldest">Oldest first</SelectItem>
                <SelectItem value="name" data-testid="sort-name">Name (A–Z)</SelectItem>
              </SelectContent>
            </Select>
            <span className="font-mono-jb text-[11px] text-[#484F58] ml-auto" data-testid="visible-count">{visible.length} shown</span>
          </div>

          {visible.length === 0 ? (
            <div className="border border-white/10 bg-[#0B0C10] p-10 text-center" data-testid="no-match">
              <div className="font-mono-jb text-sm text-[#8B949E]">No projects match this stage filter.</div>
            </div>
          ) : groupBy === "stage" ? (
            <div data-testid="projects-grid">
              {stageGroups.map(({ st, list }) => (
                <Section key={st} id={`stage-${st}`} icon={Target} title={STAGE_META[st]?.label || st} tint="#00F0FF" list={list} />
              ))}
            </div>
          ) : (
            <div data-testid="projects-grid">
              <Section id="presales" icon={Target} title="Pre-Sales — Opportunities" tint="#EAE04A" list={presalesActive} />
              <Section id="baseline" icon={ShieldCheck} title="WON — Locked Baseline" tint="#00FF41" list={baseline} />
              <Section id="assessment" icon={ShieldCheck} title="Assessment — Compliance Review" tint="#FF7A59" list={assessment} />
              <Section id="delivery" icon={Rocket} title="Delivery" tint="#00F0FF" list={delivery} />
            </div>
          )}
        </>
      )}

      {/* Transfer owner dialog (admin) */}
      <Dialog open={!!transferProj} onOpenChange={(o) => { if (!o) { setTransferProj(null); setTransferTo(""); } }}>
        <DialogContent className="bg-[#0B0C10] border-white/10 text-white" data-testid="transfer-dialog">
          <DialogHeader>
            <DialogTitle className="font-mono-jb uppercase tracking-wider text-[#00F0FF] flex items-center gap-2">
              <UserCog size={16} /> Transfer Ownership
            </DialogTitle>
          </DialogHeader>
          {transferProj && (
            <div className="space-y-4 py-2">
              <p className="text-sm text-[#8B949E] leading-relaxed">
                Reassign <span className="text-white font-mono-jb">{transferProj.name}</span> to another team member.
                Currently owned by <span className="text-[#00F0FF]">{transferProj.owner_name || transferProj.owner_email || "—"}</span>.
              </p>
              <div>
                <label className="text-xs tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">New Owner</label>
                <Select value={transferTo} onValueChange={setTransferTo}>
                  <SelectTrigger data-testid="transfer-user-select" className="mt-2 bg-[#0B0C10] border-white/15 font-mono-jb focus:ring-[#00F0FF]">
                    <SelectValue placeholder="Select a user" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0B0C10] border-white/15 text-white font-mono-jb">
                    {users.map((u) => (
                      <SelectItem key={u.id} value={u.id} data-testid={`transfer-option-${u.id}`}>
                        {u.name || u.email} — {u.role}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button data-testid="transfer-submit" onClick={doTransfer} disabled={transferring}
              className="rounded-sm bg-[#00F0FF] text-black font-mono-jb uppercase text-xs tracking-widest font-bold hover:bg-[#00F0FF]">
              {transferring ? "Transferring..." : "Transfer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
